import React from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { readRecents, rememberRecent, type RecentSearchEntry } from './recents';
import type { SearchAction, SearchResult, SearchSectionResults } from './types';
import { useUniversalSearch } from './use-universal-search';

/** A result plus the group it belongs to, which is what the key handlers move over. */
type FlatRow = { group: SearchSectionResults; result: SearchResult };

const flatten = (groups: SearchSectionResults[]): FlatRow[] =>
  groups.flatMap((group) => group.results.map((result) => ({ group, result })));

const KeyCap = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded-md border border-neutral-100 dark:border-neutral-700 px-1.5 py-1 text-xs font-medium leading-none text-neutral-300 dark:text-neutral-600">
    {children}
  </span>
);

const SkeletonBar = ({ className }: { className?: string }) => (
  <div className={clsx('animate-pulse rounded bg-neutral-100 dark:bg-neutral-700', className)} />
);

/** Placeholder for a section that has not answered yet. */
const SectionSkeleton = () => (
  <div>
    <div className="px-3 pb-1.5 pt-3">
      <SkeletonBar className="h-2.5 w-20" />
    </div>
    <div className="flex h-12 items-center gap-3 px-3">
      <SkeletonBar className="size-4 shrink-0" />
      <div className="flex flex-1 flex-col gap-1.5">
        <SkeletonBar className="h-2.5 w-1/2" />
        <SkeletonBar className="h-2 w-3/4" />
      </div>
    </div>
  </div>
);

const ActionChip = ({ action, primary }: { action: SearchAction; primary?: boolean }) => (
  <span
    className={clsx(
      'rounded-md px-2.5 py-1 text-xs font-semibold leading-none',
      primary
        ? 'bg-primary-500 text-white'
        : 'border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    )}
  >
    {action.label}
  </span>
);

const ResultRow = ({
  row,
  selected,
  onSelect,
  onRun
}: {
  row: FlatRow;
  selected: boolean;
  onSelect: () => void;
  onRun: (action: SearchAction) => void;
}) => {
  const Icon = row.group.icon;

  return (
    <div
      role="option"
      aria-selected={selected}
      onMouseEnter={onSelect}
      onClick={() => onRun(row.result.primary)}
      className={clsx(
        'flex h-12 cursor-pointer items-center gap-3 rounded-lg px-3',
        'transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
        selected
          ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300'
          : 'text-neutral-700 dark:text-neutral-300'
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0 opacity-75" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-semibold">{row.result.label}</span>
          {row.result.date && (
            <span className="shrink-0 text-[11px] font-medium text-neutral-400 dark:text-neutral-500">
              {row.result.date}
            </span>
          )}
        </div>
        {row.result.description && (
          <div className="mt-0.5 truncate text-xs font-medium text-neutral-400 dark:text-neutral-500">
            {row.result.description}
          </div>
        )}
      </div>
      {/* Actions are drawn only on the selected row: fifteen sections of chips
          would compete with the labels they belong to. */}
      <div
        className={clsx(
          'flex shrink-0 items-center gap-1.5',
          'transition-opacity duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
          selected ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        {row.result.secondary && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRun(row.result.secondary!);
            }}
          >
            <ActionChip action={row.result.secondary} />
          </button>
        )}
        <ActionChip action={row.result.primary} primary />
      </div>
    </div>
  );
};

const RecentRow = ({ entry, onRun }: { entry: RecentSearchEntry; onRun: () => void }) => (
  <button
    type="button"
    onClick={onRun}
    className="flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-neutral-700 dark:text-neutral-300 transition-colors duration-150 hover:bg-neutral-50 dark:hover:bg-white/5 motion-reduce:transition-none"
  >
    <ClockIcon aria-hidden="true" className="size-4 shrink-0 opacity-70" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold">{entry.label}</div>
      <div className="truncate text-xs font-medium text-neutral-400 dark:text-neutral-500">
        {entry.crumb}
      </div>
    </div>
  </button>
);

const GroupHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="px-3 pb-2 pt-4 text-xs font-semibold leading-none text-neutral-900 dark:text-white">
    {children}
  </div>
);

export const SearchPalette = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState(0);
  const [recents, setRecents] = React.useState<RecentSearchEntry[]>([]);
  const listRef = React.useRef<HTMLDivElement>(null);

  const { groups, pendingKeys, resolvedCount, totalCount, loading, empty } =
    useUniversalSearch(query);
  const rows = React.useMemo(() => flatten(groups), [groups]);
  const hasQuery = query.trim().length > 0;

  // Reopening starts clean: a palette that remembers the last query reopens
  // showing results for something the user has since navigated away from.
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      setRecents(readRecents());
    }
  }, [open]);

  // Results arrive section by section, so the selected index has to be pulled
  // back whenever a slower section resolves above it and shortens the list.
  React.useEffect(() => {
    setSelected((previous) => (previous >= rows.length ? 0 : previous));
  }, [rows.length]);

  React.useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selected, rows.length]);

  const runTarget = React.useCallback(
    (action: SearchAction) => {
      onClose();
      if (action.target.kind === 'external') {
        window.open(action.target.url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(action.target.to);
      }
    },
    [navigate, onClose]
  );

  const runRow = React.useCallback(
    (row: FlatRow, action: SearchAction) => {
      setRecents(rememberRecent(row.group.title, row.result));
      runTarget(action);
    },
    [runTarget]
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (rows.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((previous) => (previous + 1) % rows.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((previous) => (previous - 1 + rows.length) % rows.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runRow(rows[selected], rows[selected].result.primary);
    } else if (event.key === 'Tab') {
      const secondary = rows[selected]?.result.secondary;
      // Only claimed when the row has a second action; otherwise Tab keeps its
      // ordinary meaning and focus leaves the input.
      if (secondary) {
        event.preventDefault();
        runRow(rows[selected], secondary);
      }
    }
  };

  const statusText = !hasQuery
    ? ''
    : loading
      ? `Searching ${resolvedCount} of ${totalCount} sources`
      : `${rows.length} result${rows.length === 1 ? '' : 's'}`;

  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-neutral-900/50 transition-opacity duration-200 data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4 pt-28">
        <Headless.DialogPanel
          transition
          className={clsx(
            'flex max-h-[65vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white dark:bg-neutral-800 shadow-2xl',
            'transition duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
            'data-[closed]:scale-95 data-[closed]:opacity-0'
          )}
        >
          <div className="relative flex h-14 shrink-0 items-center gap-3 border-b border-neutral-100 dark:border-neutral-700 px-4">
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="size-5 text-primary-500 dark:text-primary-400"
            />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search Cara"
              aria-label="Search Cara"
              aria-controls="universal-search-results"
              className="flex-1 border-0 bg-transparent p-0 text-base font-medium text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0"
            />
            <div
              aria-hidden="true"
              className={clsx(
                'size-4 shrink-0 animate-spinner rounded-full border-2 border-primary-100 dark:border-primary-500/20 border-t-primary-500',
                'transition-opacity duration-200 motion-reduce:animate-none',
                loading ? 'opacity-100' : 'opacity-0'
              )}
            />
            {hasQuery && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="rounded-md border border-neutral-100 dark:border-neutral-700 px-1.5 py-1 text-xs font-medium leading-none text-neutral-400 dark:text-neutral-500 transition-colors hover:bg-neutral-50 dark:hover:bg-white/5"
              >
                Clear
              </button>
            )}
            <KeyCap>esc</KeyCap>
            {/* Fills as sections land, so a slow fan-out reads as progress
                rather than as a palette that has stopped responding. */}
            <div
              aria-hidden="true"
              className={clsx(
                'absolute inset-x-0 bottom-0 h-0.5 origin-left rounded-r bg-primary-500',
                'transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
                loading ? 'opacity-100' : 'opacity-0'
              )}
              style={{ width: `${totalCount ? (resolvedCount / totalCount) * 100 : 0}%` }}
            />
          </div>

          <div
            ref={listRef}
            id="universal-search-results"
            role="listbox"
            aria-label="Search results"
            className="min-h-0 flex-1 overflow-y-auto p-2"
          >
            {hasQuery ? (
              <>
                {groups.map((group) => (
                  <div key={group.key}>
                    <GroupHeading>{group.title}</GroupHeading>
                    {group.results.map((result) => {
                      const index = rows.findIndex(
                        (row) => row.group.key === group.key && row.result.id === result.id
                      );
                      return (
                        <ResultRow
                          key={result.id}
                          row={{ group, result }}
                          selected={index === selected}
                          onSelect={() => setSelected(index)}
                          onRun={(action) => runRow({ group, result }, action)}
                        />
                      );
                    })}
                  </div>
                ))}
                {/* One placeholder for the whole tail rather than one per
                    pending section: fifteen skeletons would be a wall. */}
                {pendingKeys.length > 0 && <SectionSkeleton />}
                {empty && (
                  <div className="px-4 py-11 text-center">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                      No results for that query
                    </div>
                    <div className="mt-1.5 text-xs font-medium text-neutral-400 dark:text-neutral-500">
                      Records you don&apos;t have access to are never indexed for you.
                    </div>
                  </div>
                )}
              </>
            ) : recents.length > 0 ? (
              <>
                <GroupHeading>Recent</GroupHeading>
                {recents.map((entry) => (
                  <RecentRow
                    key={entry.id}
                    entry={entry}
                    onRun={() => runTarget({ label: 'Open', target: entry.target })}
                  />
                ))}
              </>
            ) : (
              <div className="px-4 py-11 text-center text-xs font-medium text-neutral-400 dark:text-neutral-500">
                Search workspaces, presets, documents, emails and settings.
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-4 border-t border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-4 py-2.5 text-[11px] font-medium text-neutral-400 dark:text-neutral-500">
            <span>↑↓ Move</span>
            <span>↵ Run action</span>
            <span>⇥ Secondary</span>
            <span className="flex-1" />
            <span>{statusText}</span>
          </div>
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  );
};
